import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

const ACTION_COLORS = {
  LOGIN:           '#22c55e',
  LOGOUT:          'var(--text3)',
  CREATE:          '#3b82f6',
  UPDATE:          '#f59e0b',
  DELETE:          '#ef4444',
  PASSWORD_CHANGE: '#a855f7',
};

const ENTITY_ICONS = {
  user:         '👤',
  deal:         '📁',
  lc_sblc:      '🏦',
  counterparty: '🏢',
  bill_of_lading: '🚢',
  payment_terms: '💳',
  demurrage:    '⚓',
  incoterm:     '📋',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [skip, setSkip] = useState(0);
  const LIMIT = 100;
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async (newSkip = 0) => {
    setLoading(true);
    try {
      const params = { limit: LIMIT + 1, skip: newSkip };
      if (username) params.username = username;
      if (action) params.action = action;
      if (entityType) params.entity_type = entityType;
      const r = await API.get('/audit', { params });
      const data = r.data;
      setHasMore(data.length > LIMIT);
      setLogs(data.slice(0, LIMIT));
      setSkip(newSkip);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  }, [username, action, entityType]);

  useEffect(() => { load(0); }, [load]);

  const handleExport = async () => {
    // Build CSV from current logs
    const rows = [
      ['Timestamp', 'Username', 'Action', 'Entity Type', 'Entity ID', 'Detail', 'IP Address'],
      ...logs.map(l => [
        l.timestamp, l.username, l.action,
        l.entity_type || '', l.entity_id || '',
        l.detail || '', l.ip_address || '',
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'audit_log.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Audit Log</h1>
          <p>Wer hat was wann geändert — alle schreibenden Operationen</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={handleExport}>⬇ CSV Export</button>
      </div>

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input className="form-input" style={{ width: 180 }} placeholder="Benutzer…"
          value={username} onChange={e => setUsername(e.target.value)} />
        <select className="form-input" style={{ width: 160 }} value={action}
          onChange={e => setAction(e.target.value)}>
          <option value="">Alle Aktionen</option>
          {['LOGIN', 'CREATE', 'UPDATE', 'DELETE', 'PASSWORD_CHANGE'].map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select className="form-input" style={{ width: 160 }} value={entityType}
          onChange={e => setEntityType(e.target.value)}>
          <option value="">Alle Entitäten</option>
          {['user', 'deal', 'lc_sblc', 'counterparty', 'bill_of_lading'].map(e => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <button className="btn btn-primary btn-sm" onClick={() => load(0)}>Suchen</button>
      </div>

      {loading && <div className="loading">Lade Audit Log…</div>}

      {!loading && logs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <div>Keine Einträge gefunden.</div>
        </div>
      )}

      {!loading && logs.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Zeitstempel</th>
                <th>Benutzer</th>
                <th>Aktion</th>
                <th>Entität</th>
                <th>Detail</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const actionColor = ACTION_COLORS[log.action] || 'var(--text3)';
                const icon = ENTITY_ICONS[log.entity_type] || '📝';
                const ts = log.timestamp
                  ? new Date(log.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
                  : '—';

                return (
                  <tr key={log.id}>
                    <td style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {ts}
                    </td>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{log.username}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
                        borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: actionColor + '22', color: actionColor }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                      {log.entity_type ? (
                        <span>{icon} {log.entity_type}</span>
                      ) : '—'}
                      {log.entity_id && (
                        <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text3)', marginTop: 2 }}>
                          {log.entity_id.slice(0, 12)}…
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text2)', maxWidth: 300 }}>
                      {log.detail || '—'}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>
                      {log.ip_address || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && (logs.length > 0 || skip > 0) && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          {skip > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => load(Math.max(0, skip - LIMIT))}>
              ← Zurück
            </button>
          )}
          <span style={{ fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>
            Einträge {skip + 1}–{skip + logs.length}
          </span>
          {hasMore && (
            <button className="btn btn-ghost btn-sm" onClick={() => load(skip + LIMIT)}>
              Weiter →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
